import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { Loader2, Search } from "lucide-react";
import { keepPreviousData } from "@tanstack/react-query";
import type { GrupoDeBusca, ResultadoDeBusca } from "@bookdental/schemas";
import { Dialog, DialogContent, DialogTitle } from "@bookdental/ui";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/cn";
import { useAbrirNaOrganizacao } from "@/lib/use-abrir-na-organizacao";
import { useGruposVisiveis } from "@/components/layout/use-grupos-visiveis";

const ROTULO: Record<GrupoDeBusca | "paginas", string> = {
  paginas: "Páginas",
  pacientes: "Pacientes",
  pedidos: "Pedidos de prótese",
  pedidosRecebidos: "Pedidos recebidos",
  cadastros: "Cadastros",
  equipe: "Equipe",
  servicos: "Serviços do catálogo",
  materiais: "Materiais",
  equipamentos: "Equipamentos",
  insumos: "Insumos",
};

const LS_TODAS = "bd:busca:todas";
const lerTodas = () => {
  try {
    return localStorage.getItem(LS_TODAS) === "1";
  } catch {
    return false;
  }
};
const normalizar = (s: string) => s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

type Linha = (ResultadoDeBusca | { grupo: "paginas"; id: string; titulo: string; detalhe: string | null; destino: string }) & {
  organizationId?: string;
  organizationName?: string;
};

/**
 * Busca na plataforma inteira — a lupa da barra superior (e Ctrl+K). Páginas saem do próprio menu
 * (mesmos filtros); o resto vem de `busca.global`, que aplica a permissão de cada área em cada
 * organização. Resultado de outra organização troca pra ela ao abrir, com "Desfazer".
 */
export function BuscaGlobal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const { abrir } = useAbrirNaOrganizacao();
  const grupos = useGruposVisiveis();
  const [texto, setTexto] = useState("");
  const [termo, setTermo] = useState("");
  const [todas, setTodas] = useState(lerTodas);
  const [ativo, setAtivo] = useState(0);

  useEffect(() => {
    if (open) {
      setTexto("");
      setTermo("");
    }
  }, [open]);
  // Espera a pessoa parar de digitar — cada busca roda em várias áreas (e organizações).
  useEffect(() => {
    const t = setTimeout(() => setTermo(texto.trim()), 250);
    return () => clearTimeout(t);
  }, [texto]);

  const consulta = trpc.busca.global.useQuery(
    { termo, todas },
    { enabled: open && termo.length >= 2, placeholderData: keepPreviousData },
  );

  const linhas: Linha[] = useMemo(() => {
    if (termo.length < 2) return [];
    const t = normalizar(termo);
    const paginas = grupos
      .flatMap((g) => g.items)
      .filter((i) => normalizar(`${i.label} ${i.description ?? ""}`).includes(t))
      .slice(0, 5)
      .map((i) => ({ grupo: "paginas" as const, id: i.href, titulo: i.label, detalhe: i.description ?? null, destino: i.href }));
    return [...paginas, ...((consulta.data ?? []) as ResultadoDeBusca[])];
  }, [termo, grupos, consulta.data]);

  useEffect(() => setAtivo(0), [termo, todas]);

  const escolher = (l: Linha) => {
    onClose();
    if (l.grupo === "paginas" || !l.organizationId) navigate(l.destino);
    else abrir(l.organizationId, l.organizationName ?? "", l.destino);
  };

  const teclas = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setAtivo((i) => Math.min(i + 1, linhas.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setAtivo((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && linhas[ativo]) {
      e.preventDefault();
      escolher(linhas[ativo]);
    }
  };

  const alternarTodas = (v: boolean) => {
    setTodas(v);
    try {
      localStorage.setItem(LS_TODAS, v ? "1" : "0");
    } catch {
      // preferência só do navegador; sem armazenamento, vale até fechar
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="flex max-h-[85dvh] max-w-2xl flex-col gap-0 overflow-hidden border border-zinc-200 bg-white p-0 shadow-2xl dark:border-border dark:bg-card">
        <DialogTitle className="sr-only">Buscar na plataforma</DialogTitle>
        <div className="space-y-2 border-b border-zinc-100 p-3 pr-12 dark:border-border">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
            <input
              autoFocus
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
              onKeyDown={teclas}
              placeholder="Buscar pacientes, pedidos, cadastros, páginas..."
              aria-label="Buscar na plataforma"
              className="h-11 w-full rounded-md border border-zinc-200 bg-white pl-9 pr-9 text-base sm:text-sm dark:border-border dark:bg-card"
            />
            {consulta.isFetching && <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-zinc-400" />}
          </div>
          <label className="inline-flex cursor-pointer items-center gap-2 text-[12.5px] text-zinc-600 dark:text-muted-foreground">
            <input
              type="checkbox"
              checked={todas}
              onChange={(e) => alternarTodas(e.target.checked)}
              className="h-4 w-4 rounded border-zinc-300 accent-brand-600"
            />
            Buscar em todas as organizações
          </label>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-2">
          {termo.length < 2 ? (
            <p className="py-10 text-center text-sm text-zinc-500 dark:text-muted-foreground">
              Digite pelo menos 2 letras.
            </p>
          ) : linhas.length === 0 && !consulta.isFetching ? (
            <p className="py-10 text-center text-sm text-zinc-500 dark:text-muted-foreground">Nada encontrado.</p>
          ) : (
            <ul role="listbox" aria-label="Resultados">
              {linhas.map((l, i) => (
                <li key={`${l.grupo}-${l.organizationId ?? ""}-${l.id}`} role="option" aria-selected={i === ativo}>
                  {(i === 0 || linhas[i - 1].grupo !== l.grupo) && (
                    <p className="px-2 pb-1 pt-3 text-[10px] font-semibold uppercase tracking-wider text-zinc-400 first:pt-1 dark:text-muted-foreground/70">
                      {ROTULO[l.grupo]}
                    </p>
                  )}
                  <button
                    type="button"
                    onClick={() => escolher(l)}
                    onMouseEnter={() => setAtivo(i)}
                    className={cn(
                      "flex w-full items-center gap-3 rounded-lg px-2.5 py-2 text-left transition-colors",
                      i === ativo ? "bg-brand-50 dark:bg-primary/10" : "hover:bg-zinc-50 dark:hover:bg-muted",
                    )}
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-zinc-900 dark:text-foreground">{l.titulo}</span>
                      {l.detalhe && <span className="block truncate text-xs text-zinc-500 dark:text-muted-foreground">{l.detalhe}</span>}
                    </span>
                    {todas && l.organizationName && (
                      <span className="max-w-[40%] shrink-0 truncate rounded-full bg-zinc-100 px-2 py-0.5 text-[10.5px] font-medium text-zinc-600 dark:bg-muted dark:text-muted-foreground">
                        {l.organizationName}
                      </span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
